import React, { useEffect, useState, useCallback } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, RefreshCcw } from "lucide-react";
import { fetchAuditLog } from "@/lib/api";

const RESOURCES = [
  "All",
  "creators",
  "campaigns",
  "brands",
  "leads",
  "tasks",
  "creator_applications",
  "auth",
];

const ACTIONS = ["All", "create", "update", "delete", "login"];

const actionColor = {
  create: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  update: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  delete: "bg-red-500/10 text-red-300 border-red-500/30",
  login: "bg-purple-500/10 text-purple-300 border-purple-500/30",
};

const formatTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { hour12: false });
  } catch {
    return iso;
  }
};

const AuditLog = () => {
  const [items, setItems] = useState(null);
  const [resource, setResource] = useState("All");
  const [action, setAction] = useState("All");
  const [userEmail, setUserEmail] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = { limit: 200 };
      if (resource !== "All") params.resource = resource;
      if (action !== "All") params.action = action;
      if (userEmail.trim()) params.user_email = userEmail.trim();
      const res = await fetchAuditLog(params);
      setItems(res.items || []);
    } catch (_e) {
      setItems([]);
    }
  }, [resource, action, userEmail]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit Log</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every create / update / delete across the workspace, with the user who performed it.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          className="border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5"
          data-testid="audit-refresh-btn"
        >
          <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Select value={resource} onValueChange={setResource}>
          <SelectTrigger
            className="bg-white/[0.03] border-white/10 text-zinc-100"
            data-testid="audit-resource-select"
          >
            <SelectValue placeholder="Resource" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            {RESOURCES.map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={action} onValueChange={setAction}>
          <SelectTrigger
            className="bg-white/[0.03] border-white/10 text-zinc-100"
            data-testid="audit-action-select"
          >
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            {ACTIONS.map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Filter by user email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            className="pl-9 bg-white/[0.03] border-white/10 text-zinc-100"
            data-testid="audit-user-input"
          />
        </div>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-xl border border-white/8 bg-white/[0.02] p-12 text-center"
          data-testid="audit-empty"
        >
          <p className="text-zinc-400">No entries match those filters.</p>
        </div>
      ) : (
        <div
          className="rounded-xl border border-white/8 bg-white/[0.02] overflow-x-auto"
          data-testid="audit-table-wrap"
        >
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500 bg-white/[0.02]">
              <tr className="text-left">
                <th className="py-3 px-4 font-medium">Time</th>
                <th className="py-3 px-4 font-medium">User</th>
                <th className="py-3 px-4 font-medium">Action</th>
                <th className="py-3 px-4 font-medium">Resource</th>
                <th className="py-3 px-4 font-medium">ID</th>
                <th className="py-3 px-4 font-medium">Changed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-white/[0.02]" data-testid={`audit-row-${it.id}`}>
                  <td className="py-3 px-4 text-zinc-400 whitespace-nowrap text-xs">
                    {formatTime(it.at)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-zinc-100 text-xs">{it.user_email || "—"}</div>
                    {it.meta?.public && (
                      <div className="text-[10px] text-amber-300/80">public submission</div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                        actionColor[it.action] || "bg-zinc-500/10 text-zinc-300 border-zinc-700"
                      }`}
                    >
                      {it.action}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-zinc-300">{it.resource}</td>
                  <td className="py-3 px-4 text-zinc-400 text-xs font-mono">{it.resource_id}</td>
                  <td className="py-3 px-4 text-zinc-300 text-xs">
                    {it.meta?.changed?.length ? it.meta.changed.join(", ") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
